import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Common, color } from '@stockbit/ui';

// components
// eslint-disable-next-line import/no-unresolved
import { handleErrorAvatar } from 'utils/general';
import Image from 'global/Image';
import { LabelVerifiedAccount } from 'global/Labels';
import { AVATAR_URL } from 'constants/api';
import { effects as effectsOnboarding } from 'lib/entities/onboarding';
import { selectors as selectorsProfile } from 'lib/entities/profile';
import SearchAutocomplete from './Search';
import FollowSuggestions from './FollowSuggestions';
import UserSearchItem from './UserSearchItem';

// Store onboarding: discover people slice
import discoverPeopleSlice, {
  effects as discoverPeopleEffects,
  selectors as discoverPeopleSelectors,
} from './slice';

// styled
import OnBoardingWrapper from '../styled';
import DiscoverPeopleStyled from './styled';

// contants
import { ONBOARDING_REQUEST_FILTER } from '../constants';

const { DiscoverPeopleWrapper } = DiscoverPeopleStyled;

// icon
const { Flex, Text, Iconmoon, Avatar } = Common;

// DiscoverPeople Compoonent
function DiscoverPeople() {
  const [keyword, setKeyword] = useState<string>('');
  const [timeInterval, setTimeInterval] = useState(0);
  // request states profile
  const {
    data: dataProfile,
    // isLoading: isLoadingProfile,
    // error: errorProfile,
  } = useSelector(selectorsProfile);

  // handle actions & effects
  const dispatch = useDispatch();

  // data profile
  const { onboarding: progress } = dataProfile;

  const { getSuggestFriends, getSearchPeople, followUser } = useSelector(
    discoverPeopleSelectors,
  );

  const {
    data: dataGetSuggestFriends,
    isLoading: isLoadingDataSugggestFriends,
  } = getSuggestFriends;

  const { data: dataSearchPeople, isLoading: isLoadingDataSearchPeople } =
    getSearchPeople;

  const { people: listSearchedPeople } = dataSearchPeople;

  //  did mount
  useEffect(() => {
    // web legacy:
    // app\components\general\dumb\OnboardingPage\OnboardingStepThree\OnboardingStepThree.js
    // line 28
    dispatch(
      discoverPeopleEffects.getSuggestFriends(ONBOARDING_REQUEST_FILTER),
    );
  }, [dispatch]);

  /**
   * Go to any step in onboarding page and save it into database.
   * {web}../OnboardingPage/OnboardingPage.js
   * line: 127
   * @param {Object} event - Javascript Event object
   */
  const handleNextStep = (event) => {
    if (
      event.type === 'click' ||
      (event.type === 'keydown' && (event.key === 'Enter' || event.key === ' '))
    ) {
      // step: next onboarding step
      const nextStep = progress + 1;

      // hit API skip user/onboarding/
      dispatch(effectsOnboarding.userSkipOnboarding(parseInt(nextStep, 10)));
    }
  };

  /**
   * handlePreviousStep
   * Go to back step in onboarding page
   * replace 'onboarding' in profile state.
   * @param {Object} event - Javascript Event object
   */
  const handlePreviousStep = (event) => {
    if (
      event.type === 'click' ||
      (event.type === 'keydown' && (event.key === 'Enter' || event.key === ' '))
    ) {
      // step: previous onboarding step
      const previousStep = String(progress - 1);

      // hit API skip user/onboarding/
      dispatch(
        effectsOnboarding.userSkipOnboarding(parseInt(previousStep, 10)),
      );
    }
  };

  /**
   * handleActionFollowUnfollow function
   * dispatch follow/unfollow
   * web legacy: app\modules\profile\action.js
   * line: 480
   * @param {number} userid
   * @param {boolean} userStatusFollowed
   * @param {string} username
   * @param {string} stateKey getSuggestFriends || getSearchPeople
   */
  const handleFollow = (index, followed, userid, stateKey) => {
    const paramsData: {
      index: number;
      userid: string;
      stateKey: string;
      type: 'FOLLOW' | 'UNFOLLOW';
    } = {
      index,
      userid,
      stateKey,
      type: followed ? 'UNFOLLOW' : 'FOLLOW',
    };

    dispatch(discoverPeopleEffects.toggleFollowUser(paramsData));
  };

  /**
   * handleOnSearchPeople
   * to search searchpeople
   * web legacy:
   * app\components\general\dumb\OnboardingPage\OnboardingStepThree\OnboardingStepThree.js
   * line: 41
   */
  const handleOnSearchPeople = (valueInput) => {
    const textSearch = valueInput.trim();
    const requestBody = {
      keyword: textSearch,
      page: 1,
    };

    if (textSearch.length > 1) {
      if (timeInterval) {
        clearTimeout(timeInterval);
      }

      setTimeInterval(
        window.setTimeout(() => {
          dispatch(discoverPeopleEffects.getSearchPeople(requestBody));
        }, 1500),
      );
    }
  };

  /**
   * handleOnSelectPeople
   * to select searchedpeople
   */
  const handleOnSelectPeople = (valueInput) => {
    const textSearch = valueInput.trim();
    const requestBody = {
      keyword: textSearch,
      page: 1,
    };

    dispatch(discoverPeopleEffects.getSearchPeople(requestBody));
  };

  /**
   * onClearSearch
   * to clear all searched data
   */
  const onClearSearch = () => {
    setKeyword('');
    dispatch(discoverPeopleSlice.actions.clearSearch());
  };

  const isFetchedSearchPeople =
    !isLoadingDataSearchPeople && listSearchedPeople.length > 0;

  const options =
    isFetchedSearchPeople &&
    listSearchedPeople.map((dataPeople) => ({
      value: dataPeople.name,
      label: (
        <Flex align="center">
          <Avatar size="medium">
            <Image
              alt={dataPeople.img}
              src={`${AVATAR_URL}/${dataPeople.img}`}
              style={{
                width: 40,
                height: 40,
              }}
              onError={(e) => handleErrorAvatar(e, dataPeople.name)}
            />
          </Avatar>
          <Flex direction="column">
            <Flex align="center">
              <Text pl="10px" size="13px" family="bold">
                {dataPeople.name}
              </Text>
              <LabelVerifiedAccount
                deprecatedVerifiedStatus={dataPeople.is_verified}
                verifiedStatus={dataPeople?.verified_status}
              />
            </Flex>
            <Text pl="10px" size="13px">
              {dataPeople.desc}
            </Text>
          </Flex>
        </Flex>
      ),
    }));

  const renderSearchResult = () =>
    listSearchedPeople.map((searchedPeople, index) => (
      <UserSearchItem
        index={index}
        key={searchedPeople.id}
        dataUserSearch={searchedPeople}
        onFollow={handleFollow}
      />
    ));

  return (
    <OnBoardingWrapper>
      <div className="boarding-cover">
        <div className="boarding-content ob-discover">
          <DiscoverPeopleWrapper className="boarding-in people">
            <div className="boarding-title" style={{ marginBottom: '30px' }}>
              Discover People
            </div>
            <div className="boarding-search-wrap">
              {/* @ts-ignore */}
              <SearchAutocomplete
                onSearch={handleOnSearchPeople}
                onSelect={handleOnSelectPeople}
                options={options}
                value={keyword}
                onChange={setKeyword}
              />
              {isFetchedSearchPeople ? (
                <span
                  role="button"
                  tabIndex={0}
                  className="search-main-close"
                  onClick={onClearSearch}
                  onKeyDown={onClearSearch}
                  style={{
                    visibility: isFetchedSearchPeople ? 'visible' : 'hidden',
                  }}
                >
                  Clear Result
                </span>
              ) : (
                <div />
              )}
            </div>

            <div className="boarding-list-wrap">
              {/* {this.props.searchResult.people.length > 0 && this.renderSearchResult()}
              {this.props.searchResult.people.length === 0 && this.renderFollowSuggestions()} */}

              {isFetchedSearchPeople && renderSearchResult()}

              {listSearchedPeople.length === 0 && (
                <FollowSuggestions
                  isLoadingDataSugggestFriends={isLoadingDataSugggestFriends}
                  dataGetSuggestFriends={dataGetSuggestFriends}
                  onFollow={handleFollow}
                />
              )}
            </div>

            <div
              role="button"
              tabIndex={-1}
              className="boarding-button-next"
              data-step={progress + 1}
              onClick={handleNextStep}
              onKeyDown={handleNextStep}
            >
              Next
            </div>

            <div
              role="button"
              tabIndex={-1}
              className="boarding-button-back"
              data-step={progress - 1}
              onClick={handlePreviousStep}
              onKeyDown={handlePreviousStep}
            >
              <Iconmoon
                size="25px"
                className="icon-toolbar_prev"
                color={color.dimGray}
                hoverColor={color.lowgray}
              />
            </div>
          </DiscoverPeopleWrapper>
        </div>
      </div>
    </OnBoardingWrapper>
  );
}

export default DiscoverPeople;
